import autosize from 'autosize'
import { useField, useFormikContext } from 'formik'
import React, { useRef } from 'react'
import { useMount } from 'react-use'

import FieldError from '@/components/form/FieldError'
import FieldLabel, {
  ModalHint,
  TooltipHint,
} from '@/components/form/FieldLabel'
import HelpText from '@/components/misc/HelpText'
import { Textarea } from '@/components/ui/textarea'

export interface TextareaProps {
  children?: React.ReactNode
  name: string
  label?: React.ReactNode
  labelClass?: string
  labelSuffix?: string
  placeholder?: string
  rows?: number
  modalHint?: ModalHint
  autoFocus?: boolean
  isDisabled?: boolean
  className?: string
  tooltipHint?: TooltipHint
  helpText?: React.ReactNode | string
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void
  maxLength?: number
}

const TextareaInput: React.FC<TextareaProps> = ({
  name,
  label,
  labelClass,
  labelSuffix,
  placeholder,
  rows = 3,
  modalHint,
  autoFocus,
  isDisabled,
  className,
  tooltipHint,
  helpText,
  onChange,
  maxLength,
}) => {
  const { submitCount } = useFormikContext()
  const [field, { error }] = useField(name)
  const id = `field_${name}`
  const showError = !!(submitCount > 0 && error)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const value = field.value

  useMount(() => {
    if (textareaRef.current) {
      const textareaElement = textareaRef.current

      // Automatically adjust textarea height to the content
      autosize(textareaElement)

      // Focus the cursor at the end of the text
      const valueLength = textareaElement.value.length
      textareaElement.setSelectionRange(valueLength, valueLength)
    }
  })

  const shouldShowLabelOrMaxLength = label || maxLength

  return (
    <div>
      {shouldShowLabelOrMaxLength && (
        <div className="flex justify-between gap-2">
          {label ? (
            <FieldLabel
              htmlFor={id}
              modalHint={modalHint}
              tooltipHint={tooltipHint}
              className={labelClass}
            >
              {label}
            </FieldLabel>
          ) : (
            <div />
          )}

          {maxLength && (
            <div className="text-muted-foreground text-xxs">
              {value.length}/{maxLength}
            </div>
          )}
        </div>
      )}

      {labelSuffix && <div className="mb-2 text-xs">{labelSuffix}</div>}

      <Textarea
        id={id}
        data-field={id}
        autoFocus={autoFocus}
        disabled={isDisabled}
        placeholder={placeholder}
        ref={textareaRef}
        className={className}
        aria-invalid={showError || undefined}
        {...field}
        rows={rows}
        onChange={(event) => {
          const value = event.target.value

          if (maxLength && value.length > maxLength) {
            // Don't allow typing more than maxLength characters
            return
          }

          field.onChange(event)

          if (onChange) {
            onChange(event)
          }
        }}
      />

      {helpText && <HelpText>{helpText}</HelpText>}

      {showError && <FieldError inputId={id}>{error}</FieldError>}
    </div>
  )
}

export default TextareaInput
